// src/Dashboard/admin/layout/Topbar.jsx
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ChevronDown,
  LogOut,
  Moon,
  RefreshCw,
  Search,
  Sun,
} from "lucide-react";
import { navLabel } from "../constants/nav";
import { AvatarInitials, SearchSection } from "../components/ui";

export default function Topbar({
  activeSection,
  scopeChips = [],
  error,

  // mobile
  onOpenMobileSidebar,

  // refresh
  onRefresh,
  spinning, // boolean (loadingData || reportsLoading)

  // search popover
  searchRef,
  searchOpen,
  setSearchOpen,
  searchQuery,
  setSearchQuery,
  searchScope,
  setSearchScope,
  normalizedQuery,
  learnerSearchResults = [],
  instructorSearchResults = [],
  churchSearchResults = [],
  churchNameById,
  getChurchId,
  learnerChurchNameFallback,
  setActiveSection,

  // theme
  theme,
  setTheme,

  // profile
  profileRef,
  profileOpen,
  setProfileOpen,
  displayName,
  initials,
  signOut,
}) {
  return (
    <header className="sticky top-0 z-30 bg-white/80 dark:bg-slate-950/80 backdrop-blur border-b border-slate-200 dark:border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            className="md:hidden p-2 rounded-lg border border-slate-200 dark:border-slate-700"
            onClick={onOpenMobileSidebar}
            aria-label="Open menu"
          >
            <span className="block w-5 border-t border-slate-600 mb-1" />
            <span className="block w-5 border-t border-slate-600 mb-1" />
            <span className="block w-3 border-t border-slate-600" />
          </button>

          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">Admin</div>
            <div className="text-xs text-slate-500 truncate">{navLabel(activeSection)}</div>
          </div>

          <div className="hidden md:flex items-center gap-2 ml-2">
            <span className="text-[11px] text-slate-400">Scope:</span>
            <div className="flex flex-wrap gap-1">
              {(scopeChips || []).map((c, idx) => (
                <span
                  key={`${c}-${idx}`}
                  className="inline-flex items-center px-2 py-1 rounded-full text-[11px] bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Quick refresh */}
          <button
            type="button"
            onClick={onRefresh}
            className="hidden sm:inline-flex items-center justify-center w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Refresh"
            title="Refresh"
          >
            <RefreshCw size={18} className={spinning ? "animate-spin" : ""} />
          </button>

          {/* Search */}
          <div className="relative" ref={searchRef}>
            <button
              type="button"
              onClick={() => {
                setSearchOpen((v) => !v);
                setProfileOpen(false);
              }}
              className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
              aria-label="Search"
              title="Search ( / )"
            >
              <Search size={18} />
            </button>

            {searchOpen && (
              <div className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-[420px] bg-white dark:bg-slate-900 shadow-xl rounded-2xl border border-slate-200 dark:border-slate-800 p-3 z-50">
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <input
                      autoFocus
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search… (name, email, church)"
                      className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 dark:focus:ring-orange-500/30"
                    />
                  </div>

                  <div className="relative">
                    <select
                      value={searchScope}
                      onChange={(e) => setSearchScope(e.target.value)}
                      className="appearance-none pl-3 pr-8 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
                    >
                      <option value="all">All</option>
                      <option value="learners">Learners</option>
                      <option value="instructors">Instructors</option>
                      <option value="churches">Churches</option>
                      <option value="courses">Courses</option>
                      <option value="admins">Admins</option>
                    </select>
                    <ChevronDown
                      size={16}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                    />
                  </div>
                </div>

                <div className="mt-3 space-y-3">
                  {!normalizedQuery ? (
                    <div className="text-xs text-slate-500">
                      Tip: Press <span className="font-semibold">/</span> anytime to open search.
                    </div>
                  ) : (
                    <>
                      {(searchScope === "all" || searchScope === "learners") && (
                        <SearchSection
                          title="Learners (local scope)"
                          empty="No learner matches."
                          items={learnerSearchResults}
                          renderItem={(l) => (
                            <button
                              type="button"
                              onClick={() => {
                                setActiveSection("people-learners");
                                setSearchOpen(false);
                              }}
                              className="w-full text-left px-3 py-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                            >
                              <div className="flex items-center gap-3">
                                <AvatarInitials name={l.fullName || l.full_name || l.email || "L"} />
                                <div className="min-w-0">
                                  <div className="text-sm font-semibold truncate">
                                    {l.fullName || l.full_name || "—"}
                                  </div>
                                  <div className="text-xs text-slate-500 truncate">
                                    {l.email || "—"} •{" "}
                                    {l.churchName ||
                                      learnerChurchNameFallback?.(l) ||
                                      l.churchId ||
                                      l.church_id ||
                                      "—"}
                                  </div>
                                </div>
                              </div>
                            </button>
                          )}
                        />
                      )}

                      {(searchScope === "all" || searchScope === "instructors") && (
                        <SearchSection
                          title="Instructors (local scope)"
                          empty="No instructor matches."
                          items={instructorSearchResults}
                          renderItem={(i) => (
                            <button
                              type="button"
                              onClick={() => {
                                setActiveSection("people-instructors");
                                setSearchOpen(false);
                              }}
                              className="w-full text-left px-3 py-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                            >
                              <div className="flex items-center gap-3">
                                <AvatarInitials name={i.full_name || i.fullName || i.email || "I"} />
                                <div className="min-w-0">
                                  <div className="text-sm font-semibold truncate">
                                    {i.full_name || i.fullName || "—"}
                                  </div>
                                  <div className="text-xs text-slate-500 truncate">
                                    {i.email || "—"} •{" "}
                                    {(i.church_id || i.churchId)
                                      ? churchNameById?.get(String(i.church_id || i.churchId)) ||
                                        (i.church_id || i.churchId)
                                      : "—"}
                                  </div>
                                </div>
                              </div>
                            </button>
                          )}
                        />
                      )}

                      {(searchScope === "all" || searchScope === "churches") && (
                        <SearchSection
                          title="Churches"
                          empty="No church matches."
                          items={churchSearchResults}
                          renderItem={(c) => (
                            <div className="px-3 py-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800">
                              <div className="text-sm font-semibold truncate">{c.name}</div>
                              <div className="text-xs text-slate-500 truncate">
                                ID: {String(getChurchId?.(c))}
                              </div>
                            </div>
                          )}
                        />
                      )}

                      {searchScope !== "all" &&
                        searchScope !== "learners" &&
                        searchScope !== "instructors" &&
                        searchScope !== "churches" && (
                          <div className="text-sm text-slate-500">
                            This scope is UI-ready. Connect it to real data when available.
                          </div>
                        )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Theme toggle */}
          <button
            type="button"
            onClick={() => setTheme((prev) => (prev === "light" ? "dark" : "light"))}
            className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Toggle theme"
            title="Toggle theme"
          >
            {theme === "light" ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          {/* Profile */}
          <div className="relative" ref={profileRef}>
            <button
              type="button"
              onClick={() => {
                setProfileOpen((v) => !v);
                setSearchOpen(false);
              }}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-600 text-white font-semibold shadow-lg hover:bg-orange-700"
              title={displayName}
              aria-label="Open profile"
            >
              {initials}
            </button>

            {profileOpen && (
              <div className="absolute right-0 mt-2 w-72 max-w-[calc(100vw-2rem)] bg-white dark:bg-slate-900 shadow-xl rounded-2xl border border-slate-200 dark:border-slate-800 p-4 z-50">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-orange-600 text-white flex items-center justify-center font-semibold">
                    {initials}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{displayName}</div>
                    <div className="text-xs text-slate-500">Admin</div>
                  </div>
                </div>

                <div className="space-y-1 text-sm">
                  <Link
                    to="/profile"
                    className="block px-2 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                    onClick={() => setProfileOpen(false)}
                  >
                    Profile & Settings
                  </Link>

                  <Link
                    to="/profile/security"
                    className="block px-2 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                    onClick={() => setProfileOpen(false)}
                  >
                    Change password
                  </Link>

                  <button
                    type="button"
                    onClick={() => setTheme((prev) => (prev === "light" ? "dark" : "light"))}
                    className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    Switch to <span className="font-semibold">{theme === "light" ? "dark" : "light"} mode</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setProfileOpen(false);
                      signOut({ redirectUrl: "/login" });
                    }}
                    className="mt-2 w-full flex items-center gap-2 justify-center px-3 py-2 rounded-xl bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300"
                  >
                    <LogOut size={16} />
                    <span>Sign out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border-t border-amber-200 dark:border-amber-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center gap-2 text-xs sm:text-sm text-amber-800 dark:text-amber-100">
            <AlertCircle size={16} />
            <span className="break-words">{error}</span>
          </div>
        </div>
      )}
    </header>
  );
}
